import React, { useState, useCallback, useEffect } from "react";
import "../page/doctorTreatment.css";
import DoctorTreatmentHeader from "../component/doctorTreatmentHeader";
import DoctorTreatmentTable from "../component/doctorTreatmentTable";
import ModalDeleteConfirmation from "../../../components/modal/modalDeleteConfirmation";
import ModalAddTreatment from "../component/modalAddTreatment";
import { Constant } from "../../../helper/constant";
import { useNavigate } from "react-router-dom";
import PaginationFooter from "../../../components/footer/paginationFooter";

const DoctorTreatment = () => {
  // =====================
  // Constant Definition
  // =====================
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [totalPage, setTotalPage] = useState(1);
  const [isOpenModalAddTreatment, setIsOpenModalAddTreatment] = useState(false);
  const [selectedTreatmentData, setSelectedTreatmentData] = useState({
    doctor_treatment_id: "",
    doctor_treatment_name: "",
    doctor_treatment_price: 0,
  });
  const [page, setPage] = useState(1);
  const [listTreatmentData, setListTreatmentData] = useState(null);
  const [isOpenModalDeleteTreatment, setIsOpenModalDeleteTreatment] =
    useState(false);
  const [idTreatmentToDelete, setIdTreatmentToDelete] = useState("");
  const [searchTreatmentKeyword, setSearchTreatmentKeyword] = useState("");

  // =====================
  // Function Definition
  // =====================

  const addNewTreatment = () => {
    resetSelectedTreatmentDataValue();
    setIsOpenModalAddTreatment(true);
  };

  const onEditTreatment = (treatmentData) => {
    setSelectedTreatmentData(treatmentData);
    setIsOpenModalAddTreatment(true);
  };

  const onDeleteTreatment = (treatmentData) => {
    setIsOpenModalDeleteTreatment(true);
    setIdTreatmentToDelete(treatmentData.doctor_treatment_id);
  };

  const onDeleteTreatmentConfirmed = () => {
    setIsOpenModalDeleteTreatment(false);
    deleteTreatment();
  };

  const onCloseModalDeleteTreatment = () => {
    setIdTreatmentToDelete("");
    setIsOpenModalDeleteTreatment(false);
  };

  const onCloseModal = () => {
    resetSelectedTreatmentDataValue();
    setIsOpenModalAddTreatment(false);
  };

  const onSubmitNewTreatment = (treatmentData, isEditData) => {
    setIsOpenModalAddTreatment(false);
    if (isEditData) {
      editTreatment(treatmentData);
    } else {
      createNewTreatment(treatmentData);
    }
  };

  const resetSelectedTreatmentDataValue = () => {
    setSelectedTreatmentData({
      doctor_treatment_id: "",
      doctor_treatment_name: "",
      doctor_treatment_price: 0,
    });
  };

  const onNextPageClicked = () => {
    if (page < totalPage) {
      setPage((prevPage) => {
        return prevPage + 1;
      });
    }
  };

  const onPreviousPageClicked = () => {
    if (page !== 1) {
      setPage((prevPage) => {
        return prevPage - 1;
      });
    }
  };

  // =====================
  // Request Method
  // =====================

  const getTreatmentData = useCallback(async () => {
    try {
      setIsLoading(true);
      localStorage.setItem("searchValue", "");
      setSearchTreatmentKeyword("");
      const response = await fetch(
        Constant.BASE_API_URL + "/tindakan-medis?page=" + page,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: "bearer " + localStorage.getItem("token"),
          },
        }
      );
      if (!response.ok) {
        alert("Gagal Menerima Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
      const json = await response.json();
      const data = json.data;
      const treatments = data.doctor_treatments;
      if (!treatments) {
        setListTreatmentData([]);
      } else {
        setListTreatmentData(treatments);
      }
      const totalPage = data.meta.total_page;
      setTotalPage(totalPage);
    } catch (error) {
      setListTreatmentData([]);
    } finally {
      setIsLoading(false);
    }
  }, [page, navigate]);

  const createNewTreatment = async (treatmentData) => {
    try {
      setIsLoading(true);
      const response = await fetch(Constant.BASE_API_URL + "/tindakan-medis", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
        body: JSON.stringify({
          doctor_treatment_name: treatmentData.doctor_treatment_name,
          doctor_treatment_price: treatmentData.doctor_treatment_price,
        }),
      });
      if (!response.ok) {
        alert("Terdapat Kesalahan Dalam Memproses Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
    } finally {
      getTreatmentData();
    }
  };

  const editTreatment = async (treatmentData) => {
    try {
      setIsLoading(true);
      const treatmentId = treatmentData.doctor_treatment_id;
      const response = await fetch(
        Constant.BASE_API_URL + "/tindakan-medis/" + treatmentId,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: "bearer " + localStorage.getItem("token"),
          },
          body: JSON.stringify({
            doctor_treatment_name: treatmentData.doctor_treatment_name,
            doctor_treatment_price: treatmentData.doctor_treatment_price,
          }),
        }
      );
      if (!response.ok) {
        alert("Gagal Mengubah Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
    } finally {
      getTreatmentData();
    }
  };

  const deleteTreatment = async () => {
    try {
      setIsLoading(true);
      const treatmentId = idTreatmentToDelete;
      const response = await fetch(
        Constant.BASE_API_URL + "/tindakan-medis/" + treatmentId,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: "bearer " + localStorage.getItem("token"),
          },
        }
      );
      if (!response.ok) {
        alert("Gagal Menghapus Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
    } finally {
      setIdTreatmentToDelete("");
      getTreatmentData();
    }
  };

  const searchTreatment = (searchText) => {
    setSearchTreatmentKeyword(searchText);
    setPage(1);
  };

  const getTreatmentSearchData = useCallback(async () => {
    try {
      setIsLoading(true);

      let url = new URL(Constant.BASE_API_URL + "/tindakan-medis/search");
      url.searchParams.append("keyword", searchTreatmentKeyword);
      url.searchParams.append("page", page);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
      });
      if (!response.ok) {
        alert("Gagal Menerima Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
      const json = await response.json();
      const data = json.data;
      const treatments = data.doctor_treatments;
      const totalPage = data.meta.total_page;
      setTotalPage(totalPage);
      if (!treatments) {
        setListTreatmentData([]);
      } else {
        setListTreatmentData(treatments);
      }
    } catch (error) {
      setListTreatmentData([]);
    } finally {
      setIsLoading(false);
    }
  }, [page, searchTreatmentKeyword, navigate]);

  // =====================
  // Use Effect Method
  // =====================

  useEffect(() => {
    if (searchTreatmentKeyword !== "") {
      getTreatmentSearchData();
    } else {
      getTreatmentData();
    }
  }, [searchTreatmentKeyword, getTreatmentData, getTreatmentSearchData]);

  // =====================
  // Render Method
  // =====================

  return isLoading ? (
    <div className="loading-container">
      <div className="loader"></div>
      <p>Sedang Memuat Mohon Menunggu</p>
    </div>
  ) : (
    <div>
      <h1>Tindakan Medis</h1>
      <DoctorTreatmentHeader
        onAddTreatmentClicked={addNewTreatment}
        onSearchTreatmentEdited={searchTreatment}
      />
      <DoctorTreatmentTable
        page={page}
        treatments={listTreatmentData}
        onEditTreatmentClicked={onEditTreatment}
        onDeleteTreatmentClicked={onDeleteTreatment}
      />
      <ModalAddTreatment
        isShowModal={isOpenModalAddTreatment}
        onCloseModal={onCloseModal}
        onSubmitData={onSubmitNewTreatment}
        treatmentObject={selectedTreatmentData}
        key={Date.now()}
      />
      <ModalDeleteConfirmation
        isShowModal={isOpenModalDeleteTreatment}
        onCloseModal={onCloseModalDeleteTreatment}
        onDeleteConfirmed={onDeleteTreatmentConfirmed}
        idToDelete={idTreatmentToDelete}
      />
      <PaginationFooter
        isPreviousActive={page > 1}
        isNextActive={page < totalPage}
        onNextClick={onNextPageClicked}
        onPrevClick={onPreviousPageClicked}
      />
    </div>
  );
};

export default DoctorTreatment;
